import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import { format } from 'date-fns';
import { useAuth } from '../../hooks/useAuth';
import { StatTile } from '../../components/StatTile';
import DataTable from '../../components/DataTable';
import { RECEIVING_SERVICE } from '../../lib/serviceModel';
import { APP_URL } from '../../lib/org';

// ── Score badge ───────────────────────────────────────────────────────────────
function ScoreBadge({ score }) {
  if (score == null) return <span style={{ color: 'var(--text3)', fontSize: 12 }}>—</span>;
  const n = parseFloat(score);
  const color = n >= 4 ? 'var(--success)' : n >= 3 ? 'var(--warn)' : 'var(--danger)';
  const bg    = n >= 4 ? 'var(--success-bg)' : n >= 3 ? 'var(--warn-bg)' : 'var(--danger-bg)';
  return (
    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: bg, color }}>
      {'★'.repeat(Math.round(n))} {n.toFixed(1)}
    </span>
  );
}

// ── Recommend badge ───────────────────────────────────────────────────────────
const RECOMMEND_LABELS = {
  definitely_yes: { label: 'Definitely', color: 'var(--success)', bg: 'var(--success-bg)' },
  probably_yes:   { label: 'Probably',   color: 'var(--warn)',        bg: 'var(--warn-bg)' },
  not_sure:       { label: 'Not sure',   color: 'var(--text3)',   bg: 'var(--bg2)' },
  no:             { label: 'No',         color: 'var(--danger)',  bg: 'var(--danger-bg)' },
};

function RecommendBadge({ val }) {
  if (!val) return <span style={{ color: 'var(--text3)', fontSize: 12 }}>—</span>;
  const { label, color, bg } = RECOMMEND_LABELS[val] || { label: val, color: 'var(--text3)', bg: 'var(--bg2)' };
  return (
    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: bg, color, fontWeight: 500 }}>
      {label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export default function QADashboard() {
  const { profile } = useAuth();
  const isAdmin = ['admin', 'manager'].includes(profile?.role);

  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear,  setSelectedYear]  = useState(now.getFullYear());

  const [responses, setResponses] = useState([]);
  const [surveys,   setSurveys]   = useState([]);
  const [loading,   setLoading]   = useState(true);

  const [sortBy,  setSortBy]  = useState('submitted_at');
  const [sortAsc, setSortAsc] = useState(false);

  const [sending,       setSending]       = useState(false);
  const [sendResult,    setSendResult]    = useState(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const [pendingCount,  setPendingCount]  = useState(null);
  const [testCount,     setTestCount]     = useState(0);
  const [copied,        setCopied]        = useState(null);
  const [testMode,      setTestMode]      = useState(false);
  const [testEmail,     setTestEmail]     = useState('');
  const [resendPending, setResendPending] = useState(false);
  const [modalMode,     setModalMode]     = useState('send'); // 'send' | 'remind'

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [{ data: rData }, { data: sData }] = await Promise.all([
      supabase.from('qa_responses').select('*')
        .eq('survey_month', selectedMonth).eq('survey_year', selectedYear)
        .eq('is_test', false)
        .order('submitted_at', { ascending: false }),
      // Exclude test records from all counts and tables
      supabase.from('qa_surveys').select('*')
        .eq('survey_month', selectedMonth).eq('survey_year', selectedYear)
        .eq('is_test', false),
    ]);
    setResponses(rData || []);
    setSurveys(sData || []);
    setLoading(false);
  }, [selectedMonth, selectedYear]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Sort ──────────────────────────────────────────────────────────────────
  const sorted = [...responses].sort((a, b) => {
    const av = a[sortBy] ?? '', bv = b[sortBy] ?? '';
    return sortAsc ? (av < bv ? -1 : av > bv ? 1 : 0) : (av > bv ? -1 : av < bv ? 1 : 0);
  });

  const sorting = useMemo(() => sortBy ? [{ id: sortBy, desc: !sortAsc }] : [], [sortBy, sortAsc]);
  const handleSortingChange = (updaterOrValue) => {
    const next = typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;
    if (!next.length) return;
    setSortBy(next[0].id);
    setSortAsc(!next[0].desc);
  };

  // ── Stats ─────────────────────────────────────────────────────────────────
  const sent       = surveys.length;
  const received   = responses.length;
  const avgScore   = responses.length > 0
    ? (responses.reduce((s, r) => s + (parseFloat(r.avg_score) || 0), 0) / responses.length).toFixed(1)
    : null;
  const flagged    = responses.filter(r => r.flagged_for_followup).length;
  const responseRate = sent > 0 ? Math.round((received / sent) * 100) : 0;
  // Non-test surveys already sent this month that haven't been answered yet —
  // these are the ones a normal send silently skips (see the resend option below).
  const pendingResendCount = surveys.filter(s => s.status === 'sent').length;

  // ── Manual send — count preview ───────────────────────────────────────────
  const previewSend = async () => {
    const [{ data: activeClients }, { count: tCount }] = await Promise.all([
      supabase.from('clients')
        .select('id')
        .eq('current_status', RECEIVING_SERVICE)
        .not('guardian_email', 'is', null)
        .neq('guardian_email', ''),
      supabase.from('qa_surveys')
        .select('id', { count: 'exact', head: true })
        .eq('survey_month', selectedMonth)
        .eq('survey_year', selectedYear)
        .eq('is_test', true),
    ]);
    // Only count clients with NO qa_surveys row yet this month — a normal send
    // never touches clients who already have one, whether pending or answered.
    const existingClientIds = new Set(surveys.map(s => s.client_id));
    const newCount = (activeClients || []).filter(c => !existingClientIds.has(c.id)).length;
    setPendingCount(newCount);
    setTestCount(tCount || 0);
    setResendPending(false);
    setModalMode('send');
    setTestMode(false);
    setTestEmail('');
    setSendResult(null);
    setShowSendModal(true);
  };

  // ── Bulk reminder — nudge everyone still marked "sent" this period ────────
  const openRemindModal = () => {
    setResendPending(true);
    setModalMode('remind');
    setTestMode(false);
    setTestEmail('');
    setSendResult(null);
    setShowSendModal(true);
  };

  const confirmSend = async () => {
    if (testMode && !testEmail.trim()) return;
    setSending(true);
    try {
      const body = testMode
        ? { manual: true, test_mode: true, test_email: testEmail.trim() }
        : modalMode === 'remind'
          ? { manual: true, resend_pending: true, send_new: false, survey_month: selectedMonth, survey_year: selectedYear }
          : { manual: true, resend_pending: resendPending, survey_month: selectedMonth, survey_year: selectedYear };
      const { data, error } = await supabase.functions.invoke('send-qa-surveys', { body });
      if (error) throw error;
      setSendResult(data);
      fetchData();
    } catch (e) {
      setSendResult({ error: e.message });
    } finally {
      setSending(false);
    }
  };

  // ── Delete test records (surveys + their responses) ──────────────────────
  const deleteTestRecords = async () => {
    // Get test survey IDs for this month first
    const { data: testSurveys } = await supabase
      .from('qa_surveys')
      .select('id')
      .eq('survey_month', selectedMonth)
      .eq('survey_year', selectedYear)
      .eq('is_test', true);

    const testIds = (testSurveys || []).map(s => s.id);
    if (testIds.length === 0) return;

    // Delete associated responses first (FK constraint order)
    await supabase
      .from('qa_responses')
      .delete()
      .in('qa_survey_id', testIds);

    // Then delete the test survey rows
    await supabase
      .from('qa_surveys')
      .delete()
      .in('id', testIds);

    setTestCount(0);
    fetchData();
  };

  // ── Copy survey link ──────────────────────────────────────────────────────
  const copyLink = (token) => {
    navigator.clipboard.writeText(`${APP_URL}/qa-survey/${token}`);
    setCopied(token);
    setTimeout(() => setCopied(null), 1500);
  };

  // ── Create task manually ──────────────────────────────────────────────────
  const createFollowUpTask = async (response) => {
    const taskTitle = `QA follow-up — ${response.client_name} — ${response.period_label}`;
    const taskNotes = [
      response.avg_score != null ? `Average score: ${response.avg_score}/5` : null,
      response.answer_recommend === 'no' ? 'Parent said they would NOT recommend us' : null,
      response.answer_recommend === 'not_sure' ? 'Parent is not sure if they would recommend us' : null,
      response.feedback_improve ? `Improvement feedback: "${response.feedback_improve}"` : null,
    ].filter(Boolean).join('\n');

    const { data: task, error } = await supabase.from('tasks').insert({
      title:       taskTitle,
      notes:       taskNotes,
      priority:    response.avg_score != null && response.avg_score < 2 ? 'Urgent' : 'Normal',
      status:      'Open',
      client_id:   response.client_id,
      client_name: response.client_name,
      client_ref:  response.client_ref,
      due_date:    new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    }).select().single();

    if (!error && task) {
      await supabase.from('qa_responses')
        .update({ task_created: true, task_id: task.id })
        .eq('id', response.id);
      fetchData();
    }
  };

  const periodLabel = `${new Date(selectedYear, selectedMonth - 1, 1).toLocaleString('en-US', { month: 'long' })} ${selectedYear}`;

  const columns = [
    { id: 'client_name', accessorKey: 'client_name', header: 'Patient', size: 160, meta: { mobile: 'primary' }, cell: info => info.getValue() },
    { id: 'client_ref', accessorKey: 'client_ref', header: 'Client ID', size: 100, meta: { mono: true, mobile: 'secondary' }, cell: info => info.getValue() },
    { id: 'avg_score', accessorKey: 'avg_score', header: 'Avg score', size: 110, meta: { mobile: 'primary' }, cell: info => <ScoreBadge score={info.getValue()} /> },
    { id: 'score_overall', accessorKey: 'score_overall', header: 'Overall', size: 110, meta: { mobile: 'hidden' }, cell: info => <ScoreBadge score={info.getValue()} /> },
    { id: 'answer_recommend', accessorKey: 'answer_recommend', header: 'Recommend', size: 120, meta: { mobile: 'secondary' }, cell: info => <RecommendBadge val={info.getValue()} /> },
    {
      id: 'flagged_for_followup', accessorKey: 'flagged_for_followup', header: 'Flagged', size: 100,
      meta: { mobile: 'secondary' },
      cell: info => info.getValue()
        ? <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: 'var(--danger-bg)', color: 'var(--danger)', fontWeight: 500 }}>Flagged</span>
        : <span style={{ fontSize: 11, color: 'var(--text3)' }}>—</span>,
    },
    {
      id: 'task_created', accessorKey: 'task_created', header: 'Task', size: 110, enableSorting: false,
      meta: { mobile: 'hidden' },
      cell: info => {
        const r = info.row.original;
        if (r.task_created) return <span style={{ fontSize: 11, color: 'var(--success)' }}>✓ Created</span>;
        if (r.flagged_for_followup) return (
          <button onClick={() => createFollowUpTask(r)}
            style={{ fontSize: 11, padding: '3px 9px', borderRadius: 6, border: '0.5px solid var(--danger-border)', background: 'var(--danger-bg)', color: 'var(--danger)', cursor: 'pointer', fontFamily: 'inherit' }}>
            Create task
          </button>
        );
        return <span style={{ fontSize: 11, color: 'var(--text3)' }}>—</span>;
      },
    },
    {
      id: 'submitted_at', accessorKey: 'submitted_at', header: 'Submitted', size: 140,
      meta: { mobile: 'secondary' },
      cell: info => { const v = info.getValue(); return <span style={{ whiteSpace: 'nowrap' }}>{v ? format(new Date(v), 'MMM d, h:mm a') : '—'}</span>; },
    },
    {
      id: 'actions', header: 'Actions', size: 90, enableSorting: false,
      meta: { mobile: 'secondary' },
      cell: info => {
        const r = info.row.original;
        return (
          <button
            onClick={() => {
              const lines = [
                r.feedback_going_well ? `Going well: ${r.feedback_going_well}` : null,
                r.feedback_improve ? `Improve: ${r.feedback_improve}` : null,
              ].filter(Boolean);
              alert(lines.length ? lines.join('\n\n') : 'No open feedback provided.');
            }}
            style={{ fontSize: 11, padding: '3px 9px', borderRadius: 6, border: '0.5px solid var(--border)', background: 'transparent', color: 'var(--text3)', cursor: 'pointer', fontFamily: 'inherit' }}>
            View
          </button>
        );
      },
    },
  ];

  return (
    <div>

      {/* Controls row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <select value={selectedMonth} onChange={e => setSelectedMonth(Number(e.target.value))}
          style={{ padding: '7px 10px', borderRadius: 7, border: '0.5px solid var(--border)',
            background: 'var(--bg2)', color: 'var(--text)', fontSize: 13 }}>
          {Array.from({ length: 12 }, (_, i) => (
            <option key={i} value={i + 1}>
              {new Date(2000, i, 1).toLocaleString('en-US', { month: 'long' })}
            </option>
          ))}
        </select>
        <select value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))}
          style={{ padding: '7px 10px', borderRadius: 7, border: '0.5px solid var(--border)',
            background: 'var(--bg2)', color: 'var(--text)', fontSize: 13 }}>
          {[0, 1, 2].map(i => { const y = now.getFullYear() - i; return <option key={y} value={y}>{y}</option>; })}
        </select>

        <div style={{ flex: 1 }} />

        {isAdmin && pendingResendCount > 0 && (
          <button onClick={openRemindModal} disabled={sending}
            style={{ padding: '7px 14px', borderRadius: 8, border: '0.5px solid var(--warn-border)',
              background: 'var(--warn-bg)', color: 'var(--warn)', fontSize: 12, fontWeight: 500,
              cursor: 'pointer', fontFamily: 'inherit' }}>
            🔔 Send Reminders ({pendingResendCount})
          </button>
        )}
        {isAdmin && (
          <button onClick={previewSend} disabled={sending}
            style={{ padding: '7px 14px', borderRadius: 8, border: '0.5px solid var(--accent-border)',
              background: 'var(--accent)', color: '#fff', fontSize: 12, fontWeight: 500,
              cursor: 'pointer', fontFamily: 'inherit' }}>
            Send surveys now
          </button>
        )}
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginBottom: 16 }}>
        {[
          { label: 'Surveys sent',  value: sent, tone: 'neutral' },
          { label: 'Responses',     value: `${received} (${responseRate}%)`, tone: 'neutral' },
          { label: 'Average score', value: avgScore ? `${avgScore}/5` : '—', tone: avgScore > 0 && avgScore < 3 ? 'alert' : 'neutral' },
          { label: 'Flagged',       value: flagged, tone: flagged > 0 ? 'alert' : 'neutral' },
        ].map(s => (
          <StatTile key={s.label} label={s.label} value={s.value} tone={s.tone} />
        ))}
      </div>

      {/* Responses table */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-header">
          <div className="card-title">Responses — {periodLabel}</div>
          <span style={{ fontSize: 11, color: 'var(--text3)' }}>{received} of {sent} replied</span>
        </div>

        <DataTable
          tableId="qa-responses"
          columns={columns}
          data={sorted}
          getRowId={r => r.id}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          manualSorting
          loading={loading}
          emptyMessage={`No responses received for ${periodLabel} yet.`}
          maxHeight="calc(100vh - 380px)"
        />
      </div>

      {/* Surveys sent — pending responses */}
      {surveys.filter(s => s.status === 'sent').length > 0 && (
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="card-header">
            <div className="card-title">Awaiting response</div>
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>{surveys.filter(s => s.status === 'sent').length} pending</span>
          </div>
          {surveys.filter(s => s.status === 'sent').map(s => (
            <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 12,
              padding: '10px 16px', borderBottom: '0.5px solid var(--border)', fontSize: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500, color: 'var(--text)' }}>{s.client_name}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                  {s.guardian_email} · Sent {s.sent_at ? format(new Date(s.sent_at), 'MMM d, h:mm a') : '—'}
                </div>
              </div>
              <button
                onClick={() => copyLink(s.token)}
                title="Copy survey link"
                style={{ fontSize: 11, padding: '3px 9px', borderRadius: 6,
                  border: '0.5px solid var(--border)', background: copied === s.token ? 'var(--success-bg)' : 'transparent',
                  color: copied === s.token ? 'var(--success)' : 'var(--text3)',
                  cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                {copied === s.token ? '✓ Copied' : 'Copy link'}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Send confirmation modal */}
      {showSendModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: 'var(--card-bg)', borderRadius: 12, width: '100%', maxWidth: 440,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '0.5px solid var(--border)' }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>
                {modalMode === 'remind' ? 'Send reminders' : 'Send QA surveys now'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
                {modalMode === 'remind' ? `Nudges guardians who haven't responded for ${periodLabel}` : 'Manual trigger — bypasses last-day check'}
              </div>
            </div>
            <div style={{ padding: '20px 20px 0' }}>
              {!sendResult ? (
                <>
                  {/* Test mode toggle */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14,
                    padding: '10px 12px', borderRadius: 8, border: '0.5px solid var(--border)',
                    background: 'var(--bg2)', cursor: 'pointer' }}
                    onClick={() => setTestMode(m => !m)}>
                    <div style={{
                      width: 36, height: 20, borderRadius: 10, flexShrink: 0, transition: 'background 0.2s',
                      background: testMode ? 'var(--accent)' : 'var(--border)', position: 'relative',
                    }}>
                      <div style={{
                        position: 'absolute', top: 2, left: testMode ? 18 : 2,
                        width: 16, height: 16, borderRadius: '50%', background: '#fff',
                        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      }} />
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>Test mode</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)' }}>Send one test email to a specific address — no patient records touched</div>
                    </div>
                  </div>

                  {testMode ? (
                    <>
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>Send test survey to</div>
                        <input
                          type="email"
                          value={testEmail}
                          onChange={e => setTestEmail(e.target.value)}
                          placeholder="your@email.com"
                          autoFocus
                          style={{ width: '100%', padding: '9px 12px', borderRadius: 7,
                            border: '1px solid var(--border)', background: 'var(--bg2)',
                            color: 'var(--text)', fontSize: 13, fontFamily: 'inherit',
                            boxSizing: 'border-box' }}
                        />
                      </div>
                      <div style={{ background: 'var(--accent-bg)', border: '0.5px solid var(--accent-border)',
                        borderRadius: 7, padding: '10px 12px', fontSize: 12, color: 'var(--accent)', marginBottom: 16 }}>
                        ✓ Uses the first active patient's name for personalisation. The survey link will be fully functional. No emails sent to real guardians.
                      </div>
                    </>
                  ) : modalMode === 'remind' ? (
                    <>
                      <div style={{ fontSize: 13, color: 'var(--text)', marginBottom: 16, lineHeight: 1.5 }}>
                        This will send a reminder email to{' '}
                        <strong>{pendingResendCount} guardian{pendingResendCount !== 1 ? 's' : ''}</strong> who
                        haven't responded to the {periodLabel} survey yet. Guardians who already answered won't be contacted.
                      </div>
                      <div style={{ background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)',
                        borderRadius: 7, padding: '10px 12px', fontSize: 12, color: 'var(--danger)', marginBottom: 16 }}>
                        ⚠ This sends real emails. Use test mode above to send just to yourself first.
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: 13, color: 'var(--text)', marginBottom: 16, lineHeight: 1.5 }}>
                        This will send the {periodLabel} survey email to{' '}
                        <strong>{pendingCount ?? '…'} active patient{pendingCount !== 1 ? 's' : ''}</strong> who
                        haven't already received a survey this month.
                      </div>

                      {pendingResendCount > 0 && (
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 14,
                          padding: '10px 12px', borderRadius: 8, border: '0.5px solid var(--warn-border)',
                          background: 'var(--warn-bg)', cursor: 'pointer' }}
                          onClick={() => setResendPending(r => !r)}>
                          <input type="checkbox" checked={resendPending} onChange={() => {}} style={{ marginTop: 2 }} />
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>
                              Also resend to {pendingResendCount} guardian{pendingResendCount !== 1 ? 's' : ''} who haven't responded yet
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                              Sends a fresh link with a new expiry to everyone still marked "sent" for {periodLabel} — skips anyone who already answered.
                            </div>
                          </div>
                        </div>
                      )}

                      <div style={{ background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)',
                        borderRadius: 7, padding: '10px 12px', fontSize: 12, color: 'var(--danger)', marginBottom: 16 }}>
                        ⚠ This sends real emails to all active guardians. Use test mode above to send just to yourself first.
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div style={{ fontSize: 13, color: 'var(--text)', marginBottom: 16 }}>
                  {sendResult.error
                    ? <span style={{ color: 'var(--danger)' }}>Error: {sendResult.error}</span>
                    : (
                      <span style={{ color: 'var(--success)' }}>
                        ✓ Sent {sendResult.sent} of {sendResult.total} surveys
                        {sendResult.resent > 0 ? ` (${sendResult.resent} resent to guardians still pending)` : ''}
                      </span>
                    )
                  }
                </div>
              )}
            </div>
            <div style={{ padding: '12px 20px 20px', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => { setShowSendModal(false); setSendResult(null); }} className="btn">
                {sendResult ? 'Close' : 'Cancel'}
              </button>
              {!sendResult && (
                <button
                  onClick={confirmSend}
                  disabled={sending || (testMode && !testEmail.trim())}
                  className="btn btn-primary">
                  {sending
                    ? 'Sending…'
                    : testMode
                      ? `Send test to ${testEmail || '…'}`
                      : modalMode === 'remind'
                        ? `Send reminder to ${pendingResendCount} guardian${pendingResendCount !== 1 ? 's' : ''}`
                        : resendPending
                          ? `Send to ${pendingCount ?? '…'} new + resend to ${pendingResendCount} pending`
                          : `Send to ${pendingCount ?? '…'} patients`}
                </button>
              )}
            </div>
            {!sendResult && testCount > 0 && (
              <div style={{ padding: '0 20px 16px', borderTop: '0.5px solid var(--border)', paddingTop: 12 }}>
                <button
                  onClick={async () => { await deleteTestRecords(); setTestCount(0); }}
                  style={{ fontSize: 11, color: 'var(--danger)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>
                  🗑 Delete {testCount} test record{testCount !== 1 ? 's' : ''} from {periodLabel}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
